'use client';

import { useEffect, type CSSProperties } from 'react';
import { analytics } from '@/lib/analytics';

/**
 * Last-resort boundary: renders *instead of* the root layout when the layout
 * itself (or anything global-error escalates from) throws. Nothing from the
 * app shell can be assumed here — no Tailwind, no i18n provider, no fonts —
 * so this screen is deliberately self-contained: inline styles, system font
 * stack, English only. It must render on the oldest webviews we support.
 *
 * analytics.captureException works even here: it lazily initialises PostHog
 * (lib/posthog-browser.ts) rather than relying on app/providers.tsx, which
 * never mounts on this code path.
 */

const page: CSSProperties = {
  margin: 0,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#000',
  color: '#fff',
  fontFamily:
    "'Arial', -apple-system, system-ui, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, sans-serif",
  textAlign: 'center',
};

const column: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '0 16px',
};

const heading: CSSProperties = {
  margin: 0,
  fontSize: '40px',
  fontWeight: 700,
  textTransform: 'uppercase',
  // Offset tri-color layers, same treatment as the 404 page's glyph.
  textShadow: '3px 0 #FF0000, -3px 0 #0000FF, 0 3px #FFFF00',
};

const message: CSSProperties = {
  margin: 0,
  color: '#a1a1aa', // zinc-400
  maxWidth: '28rem',
  lineHeight: 1.5,
};

const digestStyle: CSSProperties = {
  margin: 0,
  color: '#71717a', // zinc-500
  fontSize: '12px',
  fontFamily: 'monospace',
};

const buttonRow: CSSProperties = {
  display: 'flex',
  gap: '16px',
  marginTop: '8px',
};

const buttonBase: CSSProperties = {
  display: 'inline-block',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'uppercase',
  textDecoration: 'none',
  cursor: 'pointer',
  border: '1px solid #fff',
};

const primaryButton: CSSProperties = {
  ...buttonBase,
  backgroundColor: '#fff',
  color: '#000',
};

const secondaryButton: CSSProperties = {
  ...buttonBase,
  backgroundColor: 'transparent',
  color: '#fff',
};

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    analytics.captureException(error, { boundary: 'global-error', digest: error.digest });
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Something went wrong · TONE3000</title>
      </head>
      <body style={page}>
        <main style={column}>
          <h1 style={heading}>Something went wrong</h1>
          <p style={message}>
            An unexpected error occurred. It&apos;s been reported — reload the page, or head back
            home.
          </p>
          {error.digest && <p style={digestStyle}>Error code: {error.digest}</p>}
          <div style={buttonRow}>
            <button type="button" style={primaryButton} onClick={() => window.location.reload()}>
              Reload
            </button>
            <a href="/" style={secondaryButton}>
              Return Home
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
